import { useMemo } from 'react';
import { measureSheet, readScaleHints, type ScaleSource } from '@drawing2d/core';
import type { RunResult } from '../pipeline.js';
import { t, fill, type Lang } from '../i18n.js';

/**
 * The one question the file cannot answer for itself.
 *
 * Testing the hosted prototype produced three complaints that were all this one
 * panel: "what does resolve scale mean", "how does it auto resolve when clicked",
 * and "what are 1:1 1:2". Every one was fair. The panel asked for a paper size
 * and a ratio with no explanation, under a paragraph about never guessing, behind
 * a button labelled *Resolve scale* — which reads as an instruction to the
 * software, not as "apply what I just chose". The software resolved nothing.
 *
 * So three things changed:
 *
 *  - **The sheet size is no longer asked for.** A PDF point is 1/72 inch by
 *    definition, so the page states its own physical size and the operator was
 *    being asked to re-enter it. That was not merely redundant: the selector
 *    defaulted to A4 for any dropped file, so an A3 drawing silently produced a
 *    DXF at 71% of true size — the precise failure the never-guess rule exists to
 *    prevent, reachable in two clicks.
 *  - **The button names its outcome**, "Produce the DXF at 1:1", so it cannot be
 *    read as detection.
 *  - **The drawing's own printed scale is offered.** The title block usually says
 *    it, and refusing to even *show* that while demanding it be typed is what
 *    made the panel feel obtuse. It is a suggestion with a visible source and one
 *    click to accept, so a person still decides — which is the difference between
 *    offering an answer and guessing one.
 */
export function ScaleStep({
  result,
  lang,
  drawingScale,
  onDrawingScale,
  onResolve,
}: {
  result: RunResult;
  lang: Lang;
  drawingScale: string;
  onDrawingScale: (value: string) => void;
  onResolve: (source: ScaleSource) => void;
}): JSX.Element {
  const s = (k: Parameters<typeof t>[0]): string => t(k, lang);
  const page = result.document.pages[0];
  const sheet = useMemo(
    () => (page ? measureSheet(page.widthPt, page.heightPt) : null),
    [page],
  );
  // The first ratio the sheet states. A title block gives the sheet's scale
  // before any detail view overrides it, and document order is reading order.
  const printed = useMemo(() => readScaleHints(result.document)[0], [result.document]);

  const ratio = parseRatio(drawingScale);

  if (result.document.scaleState === 'resolved') {
    return (
      <section className="step step-done">
        <h2>
          {s('stepDxf')} <span className="step-state">{s('dxfReady')}</span>
        </h2>
        {/* All values, so the whole line is set in the mono. */}
        <p className="step-facts values">
          {sheetLabel(sheet, lang)} · {drawingScale} ·{' '}
          {result.document.mmPerPoint?.toFixed(6)} mm/pt
        </p>
      </section>
    );
  }

  return (
    <section className="step step-todo">
      <h2>
        {s('stepDxf')} <span className="step-state">{s('scaleUnknown')}</span>
      </h2>
      <p className="step-lead">{s('dxfNeedsScale')}</p>

      <p className="step-facts">
        {s('sheetIs')} <b>{sheetLabel(sheet, lang)}</b>
        <span className="step-aside">{s('sheetMeasuredNote')}</span>
      </p>

      <label className="step-field">
        {s('drawnAtScale')}
        <select value={drawingScale} onChange={(e) => onDrawingScale(e.target.value)}>
          {RATIOS.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </label>

      {/*
        Always say what the sheet says, even when it already agrees.
        Showing the reading only on a disagreement makes the software look as
        though it never looked — which is what "how does it auto resolve the
        scale?" was really asking. The offer to accept it appears only when it
        differs, because there is nothing to accept otherwise.
      */}
      {printed && (
        <p className="step-hint" data-hint="printed-scale">
          {s('drawingSays')} <b>{printed.sourceText}</b>
          {printed.text === drawingScale ? (
            <span className="agrees"> {s('scaleAgrees')}</span>
          ) : (
            <button className="link" onClick={() => onDrawingScale(printed.text)}>
              {s('useThis')} → {printed.text}
            </button>
          )}
        </p>
      )}

      <button
        className="primary wide"
        disabled={ratio === null}
        onClick={() => ratio !== null && onResolve({ kind: 'sheet', drawingScale: ratio })}
      >
        {fill(s('produceAt'), { scale: drawingScale })}
      </button>

      <details className="step-explain">
        <summary>{s('whatIsScale')}</summary>
        <p>{s('scaleMeaning')}</p>
        <ul>
          <li>{s('scaleOneToOne')}</li>
          <li>{s('scaleOneToTwo')}</li>
          <li>{s('scaleTwoToOne')}</li>
        </ul>
        <p className="note">{s('scaleWhyAsk')}</p>
        <p className="note">{s('orMeasure')}</p>
      </details>
    </section>
  );
}

/** The ratios a mechanical drawing is issued at, per JIS Z 8314. */
const RATIOS = ['1:1', '1:2', '1:5', '1:10', '1:20', '1:50', '1:100', '2:1', '5:1', '10:1'];

/**
 * `a:b` as the multiplier `resolveScale` takes: real millimetres per millimetre
 * of paper. 1:2 is drawn at half size, so a paper millimetre is two real ones.
 */
function parseRatio(text: string): number | null {
  const [a, b] = text.split(':').map(Number);
  if (!a || !b || !(a > 0) || !(b > 0)) return null;
  return b / a;
}

function sheetLabel(
  sheet: ReturnType<typeof measureSheet> | null,
  lang: Lang,
): string {
  if (!sheet) return '—';
  const size = `${Math.round(sheet.widthMm)} × ${Math.round(sheet.heightMm)} mm`;
  return sheet.name === null ? `${size} — ${t('sheetUnrecognised', lang)}` : `${sheet.name}, ${size}`;
}
